import React, { useMemo, useState } from "react"
import { defineQuery, QueryableResults, QueryFilter } from "@nova/queryable"
import { usePilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { Card } from "features/pilot-tracking/MetricLayout"
import { Status } from "@nova/ds-component-status"
import { servicesQuery } from "./AircraftStatusQueries"
import { AddMaintenanceRecordModal } from "./AddMaintenanceRecordModal"
import { ButtonTertiary, IconButton } from "@nova/component-btn"
import { useRouter } from "pages"
import * as Icons from "@nova/component-icons"

const accumulatedMissionsQuery = defineQuery((aircraftId: string, sinceDate?: string | null) => {
    const filters: QueryFilter<"metadata">[] = [
        { field: "mission.aircraft_id", op: "=", value: aircraftId },
    ]

    if (sinceDate) {
        filters.push({ field: "started_at", op: ">", value: sinceDate })
    }

    return {
        resource: "metadata",
        aggregates: [
            { field: "mission_id", func: "COUNT", alias: "flight_count" },
            { field: "duration", func: "SUM", alias: "total_seconds" },
        ],
        filters,
    }
})

export type ServiceLink = QueryableResults<typeof servicesQuery>[number]

export const AircraftMaintenanceServicesSection = ({
    aircraftId,
    data,
    cardRecordData,
}: {
    aircraftId: string
    data: ServiceLink[]
    cardRecordData?: Record<string, { last_performed: string | null }[]>
}) => {
    const router = useRouter("/workspace/:workspaceId")

    return (
        <div className="flex flex-col gap-4">
            <div className="flex justify-between items-end">
                <h2 className="text-white font-medium">Services</h2>
                <router.Link to={{ path: "/fleet/maintenance" }}>
                    <IconButton className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors">
                        Go to Maintenance
                        <Icons.SmallIcon Icon={Icons.ArrowRight} />
                    </IconButton>
                </router.Link>
            </div>
            <div className="flex flex-wrap gap-4">
                {data.map((link) => (
                    <ServiceCardContainer
                        key={link.template_id as string}
                        link={link}
                        aircraftId={aircraftId}
                        lastPerformedIso={
                            cardRecordData?.[link.template_id as string]?.[0]?.last_performed ??
                            null
                        }
                    />
                ))}
            </div>

            {!data.length && (
                <div className="flex flex-col justify-center items-center gap-2 text-center py-12">
                    <Icons.ExtraLargeIcon className="text-white-200" Icon={Icons.CheckCircle} />
                    <h2 className="text-[30px] text-gray-0">No upcoming maintenance</h2>
                    <h5 className="text-gray-100 max-w-[323px]">
                        All service conditions are within limits. This drone is clear of overdue or
                        due-soon services.
                    </h5>
                </div>
            )}
        </div>
    )
}

const ServiceCardContainer = ({
    link,
    aircraftId,
    lastPerformedIso,
}: {
    link: ServiceLink
    aircraftId: string
    lastPerformedIso: string | null
}) => {
    const missionsQueryDef = useMemo(
        () => accumulatedMissionsQuery(aircraftId, lastPerformedIso),
        [aircraftId, lastPerformedIso]
    )

    const { data: missionsData } = usePilotTrackingQuery(missionsQueryDef)

    const currentFlights = (missionsData?.[0]?.flight_count as number) || 0
    const totalSeconds = (missionsData?.[0]?.total_seconds as number) || 0
    const currentHours = Math.round(totalSeconds / 3600)

    let currentDays = 0
    if (lastPerformedIso) {
        const now = new Date()
        const last = new Date(lastPerformedIso)
        currentDays = Math.floor((now.getTime() - last.getTime()) / (1000 * 3600 * 24))
    }

    return (
        <ServiceCardUI
            link={link}
            aircraftId={aircraftId}
            currentFlights={currentFlights}
            currentHours={currentHours}
            currentDays={currentDays}
        />
    )
}

type MetricCalc = {
    type: "Flights" | "Hours" | "Days"
    current: number
    limit: number
    pct: number
}

const ServiceCardUI = ({
    link,
    aircraftId,
    currentFlights,
    currentHours,
    currentDays,
}: {
    link: ServiceLink
    aircraftId: string
    currentFlights: number
    currentHours: number
    currentDays: number
}) => {
    const [modalOpen, setModalOpen] = useState(false)
    const limitFlights = link["template.condition_usage_flights"] as number | null
    const limitHours = link["template.condition_duration_hours"] as number | null
    const limitDays = link["template.condition_interval_value"] as number | null

    // Gather active metrics to figure out which one is the "primary" (closest to due)
    const metrics: MetricCalc[] = []
    if (limitFlights != null)
        metrics.push({
            type: "Flights",
            current: currentFlights,
            limit: limitFlights,
            pct: currentFlights / limitFlights,
        })
    if (limitHours != null)
        metrics.push({
            type: "Hours",
            current: currentHours,
            limit: limitHours,
            pct: currentHours / limitHours,
        })
    if (limitDays != null)
        metrics.push({
            type: "Days",
            current: currentDays,
            limit: limitDays,
            pct: currentDays / limitDays,
        })

    if (metrics.length === 0) return null

    const primary = metrics.reduce((max, m) => (m.pct > max.pct ? m : max), metrics[0])

    const isOverdue = primary.pct >= 1
    const isDueSoon = primary.pct >= 0.8 && !isOverdue

    const statusText = isOverdue ? "Overdue" : isDueSoon ? "Due soon" : null
    const fillPct = Math.min(100, Math.max(0, primary.pct * 100))
    const barGradient = isOverdue
        ? "linear-gradient(to right, #321007, #983115)"
        : isDueSoon
          ? "linear-gradient(to right, #351E09, #985415)"
          : "#FFFFFF33"
    const valueColor = isOverdue ? "text-red-200" : isDueSoon ? "text-orange-200" : "text-gray-100"

    const displayLabel = primary.type === "Hours" ? "Flight Hours" : primary.type
    const overdueReason =
        primary.type === "Flights"
            ? "Flight threshold reached"
            : primary.type === "Hours"
              ? "Flight hour threshold reached"
              : "Scheduled interval reached"
    const remaining = primary.limit - primary.current
    const message = isOverdue
        ? overdueReason
        : `${Math.ceil(remaining)} ${primary.type.toLowerCase()} remaining`

    return (
        <Card className="flex flex-col justify-between w-[340px]">
            <div className="flex justify-between items-center mb-4">
                <h4 className="text-white text-base truncate pr-2">
                    {link["template.name"] as string}
                </h4>
                {statusText && (
                    <div className="shrink-0">
                        <Status type={isOverdue ? "error" : "warning"} variant="outline">
                            {statusText}
                        </Status>
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-1 mb-4">
                <div className="flex justify-between">
                    <h5 className="text-gray-400">{displayLabel}</h5>
                    <h5 className={`${isOverdue || isDueSoon ? "font-bold" : ""} ${valueColor}`}>
                        {primary.current}/{primary.limit}
                    </h5>
                </div>
                <div className="h-2 w-full bg-gray-700 rounded-full overflow-hidden">
                    <div
                        className="h-full rounded-full"
                        style={{ width: `${fillPct}%`, background: barGradient }}
                    />
                </div>
                <small className="text-gray-100 mt-1">{message}</small>
            </div>

            <div className="flex items-center gap-5 text-gray-400 mb-6">
                {metrics.map((m) => (
                    <span key={m.type} className="flex items-center gap-1">
                        <small className="text-gray-300">{m.type}: </small>
                        <small className="text-gray-100">
                            {m.current}/{m.limit}
                        </small>
                    </span>
                ))}
            </div>

            <ButtonTertiary
                onClick={() => setModalOpen(true)}
                title="Add Record"
                className="w-full"
            />

            {modalOpen && (
                <AddMaintenanceRecordModal
                    aircraftId={aircraftId}
                    templateId={link.template_id}
                    templateInstructions={link["template.instructions"] as string | null}
                    onClose={() => setModalOpen(false)}
                />
            )}
        </Card>
    )
}
